import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Divider,
  Drawer,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  fetchWatchtowerCampaigns,
  fetchWatchtowerAdsets,
  fetchWatchtowerAds,
} from '../services/watchtower';
import {
  selectWatchtowerState,
  WATCHTOWER_CACHE_TTL_MS,
  parseWatchtowerCacheKey,
} from '../store/watchtowerSlice';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const currencyCentsFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7d', label: 'Last 7 days' },
  { value: 'last_30d', label: 'Last 30 days' },
  { value: 'last_90d', label: 'Last 90 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'lifetime', label: 'Lifetime' },
];

const EFFECTIVE_STATUSES_ALL = [
  'ACTIVE',
  'PAUSED',
  'ARCHIVED',
  'WITH_ISSUES',
  'IN_PROCESS',
];

const pickFirstDefined = (...values) =>
  values.find((value) => value !== null && value !== undefined);

const formatCurrencyValue = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return typeof value === 'string' && value.trim().length > 0 ? value : '—';
  }
  const normalized = Math.abs(numeric) >= 1000 ? numeric / 100 : numeric;
  return currencyFormatter.format(normalized);
};

const formatCurrencyWithCents = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return typeof value === 'string' && value.trim().length > 0 ? value : '—';
  }
  return currencyCentsFormatter.format(numeric);
};

const formatNumberValue = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return typeof value === 'string' && value.trim().length > 0 ? value : '—';
  }
  return numberFormatter.format(numeric);
};

const formatPercentValue = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return typeof value === 'string' && value.trim().length > 0 ? value : '—';
  }
  return `${numeric.toFixed(2)}%`;
};

const truncateText = (value, maxLength = 48) => {
  if (!value) {
    return value;
  }
  return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
};

const summarizeUrlParams = (value) => {
  if (!value) {
    return '—';
  }
  if (typeof value === 'string') {
    return truncateText(value);
  }
  if (Array.isArray(value)) {
    return truncateText(value.join('&'));
  }
  if (typeof value === 'object') {
    const parts = Object.entries(value).map(([key, val]) => `${key}=${val}`);
    return truncateText(parts.join('&'));
  }
  return String(value);
};

const extractMetric = (snapshot, field) => {
  if (!snapshot) {
    return undefined;
  }
  const metrics = snapshot.metrics;
  if (metrics && metrics[field] !== undefined && metrics[field] !== null) {
    return metrics[field];
  }
  const insights = snapshot.insights;
  if (!insights) {
    return undefined;
  }
  if (insights[field] !== undefined && insights[field] !== null) {
    return insights[field];
  }
  if (Array.isArray(insights.data)) {
    const matchingRow = insights.data.find(
      (row) => row && row[field] !== undefined && row[field] !== null
    );
    if (matchingRow) {
      return matchingRow[field];
    }
  }
  return undefined;
};

const describeValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
};

const uniqueArray = (values) => Array.from(new Set(values.filter(Boolean)));

const splitFieldPath = (path) => {
  if (!path) {
    return [];
  }
  if (path.includes('.')) {
    return path.split('.').filter(Boolean);
  }
  return [path];
};

const getNestedValue = (object, path) => {
  if (!object || !path) {
    return undefined;
  }
  const parts = Array.isArray(path) ? path : splitFieldPath(path);
  return parts.reduce((accumulator, key) => {
    if (accumulator === null || accumulator === undefined) {
      return undefined;
    }
    return accumulator[key];
  }, object);
};

const resolveSnapshotValue = (snapshot, field, kind) => {
  if (!snapshot) {
    return undefined;
  }

  const candidates = uniqueArray([
    field,
    field?.replace(`${kind}_`, ''),
    field?.replace(/^campaign_/, ''),
    field?.replace(/^adset_/, ''),
    field?.replace(/^ad_/, ''),
    field?.replace(/_/g, '.'),
  ]);

  const buckets = uniqueArray([
    snapshot.computed,
    snapshot.metrics,
    snapshot[kind],
    snapshot.campaign,
    snapshot.adset,
    snapshot.ad,
    snapshot,
  ]);

  for (const bucket of buckets) {
    if (!bucket) {
      continue;
    }
    for (const candidate of candidates) {
      if (!candidate) {
        continue;
      }
      if (bucket.hasOwnProperty(candidate)) {
        const direct = bucket[candidate];
        if (direct !== undefined && direct !== null) {
          return direct;
        }
      }
      const nested = getNestedValue(bucket, candidate);
      if (nested !== undefined && nested !== null) {
        return nested;
      }
    }
  }
  return undefined;
};

const bandToColor = (band) => {
  switch (band) {
    case 'clean':
      return 'success';
    case 'minor':
      return 'info';
    case 'attention':
      return 'warning';
    case 'action':
      return 'error';
    default:
      return 'default';
  }
};

const severityToColor = (severity) => {
  if (!severity) {
    return 'default';
  }
  const normalized = severity.toLowerCase();
  if (normalized === 'blocker' || normalized === 'critical' || normalized === 'high') {
    return 'error';
  }
  if (normalized === 'major' || normalized === 'warning' || normalized === 'medium') {
    return 'warning';
  }
  if (normalized === 'minor' || normalized === 'low') {
    return 'info';
  }
  return 'default';
};

const formatScore = (score) => {
  if (score === null || score === undefined) {
    return '—';
  }
  return Math.round(score);
};

const getPerformanceMetrics = (snapshot) => ({
  spend: extractMetric(snapshot, 'spend'),
  impressions: extractMetric(snapshot, 'impressions'),
  clicks: extractMetric(snapshot, 'clicks'),
  linkClicks: extractMetric(snapshot, 'inline_link_clicks'),
  reach: extractMetric(snapshot, 'reach'),
  ctr: extractMetric(snapshot, 'ctr'),
  cpc: extractMetric(snapshot, 'cpc'),
  cpm: extractMetric(snapshot, 'cpm'),
  engagements: extractMetric(snapshot, 'engagements'),
});

const renderPerformanceCell = (metrics) => {
  if (!metrics) {
    return '—';
  }
  const clicksValue = pickFirstDefined(metrics.linkClicks, metrics.clicks);
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        Impr: {formatNumberValue(metrics.impressions)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Clicks: {formatNumberValue(clicksValue)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        CTR: {formatPercentValue(metrics.ctr)}
      </Typography>
    </Stack>
  );
};

const renderMetricsGrid = (metrics) => {
  if (!metrics) {
    return null;
  }
  const clicksValue = pickFirstDefined(metrics.linkClicks, metrics.clicks);
  const items = [
    { label: 'Impressions', value: formatNumberValue(metrics.impressions) },
    { label: 'Clicks', value: formatNumberValue(clicksValue) },
    { label: 'CTR', value: formatPercentValue(metrics.ctr) },
    { label: 'CPC', value: formatCurrencyWithCents(metrics.cpc) },
    { label: 'CPM', value: formatCurrencyWithCents(metrics.cpm) },
    { label: 'Reach', value: formatNumberValue(metrics.reach) },
    { label: 'Engagements', value: formatNumberValue(metrics.engagements) },
  ];

  return (
    <Box
      sx={{
        mt: 1.5,
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
        gap: 1.5,
      }}
    >
      {items.map((item) => (
        <Box key={item.label}>
          <Typography variant="caption" color="text.secondary">
            {item.label}
          </Typography>
          <Typography variant="body2">{item.value}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const WatchtowerDashboard = () => {
  const dispatch = useDispatch();
  const watchtower = useSelector(selectWatchtowerState);
  const {
    campaigns,
    adsetsByCampaign,
    adsByAdset,
    snapshots,
  } = watchtower;

  const [selectedCampaignKey, setSelectedCampaignKey] = useState(null);
  const [selectedAdsetKey, setSelectedAdsetKey] = useState(null);
  const [campaignsCollapsed, setCampaignsCollapsed] = useState(false);
  const [violationTarget, setViolationTarget] = useState(null);
  const [datePreset, setDatePreset] = useState('last_7d');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  // TODO: consider restructuring state to support collapsible sections + violation side panel without over-fetching.
  const DEFAULT_PAGE_SIZE = 25;
  const datePresetRef = useRef(datePreset);
  const effectiveStatus = useMemo(
    () => (activeOnly ? 'ACTIVE' : EFFECTIVE_STATUSES_ALL.join(',')),
    [activeOnly]
  );
  const effectiveStatusRef = useRef(effectiveStatus);

  useEffect(() => {
    const lastTiming = campaigns.lastFetched ?? campaigns.lastAttempted;
    const ttlExpired =
      !lastTiming || Date.now() - lastTiming > WATCHTOWER_CACHE_TTL_MS;
    const presetMismatch =
      Boolean(campaigns.datePreset && campaigns.datePreset !== datePreset);
    const statusMismatch =
      Boolean(campaigns.effectiveStatus && campaigns.effectiveStatus !== effectiveStatus);
    const shouldFetchCampaigns = ttlExpired || presetMismatch || statusMismatch;
    if (shouldFetchCampaigns && !campaigns.loading) {
      dispatch(
        fetchWatchtowerCampaigns({
          params: {
            page_size: campaigns.pageSize || DEFAULT_PAGE_SIZE,
            date_preset: datePreset,
            effective_status: effectiveStatus,
          },
        })
      );
    }
  }, [
    campaigns.lastAttempted,
    campaigns.lastFetched,
    campaigns.loading,
    campaigns.pageSize,
    campaigns.datePreset,
    campaigns.effectiveStatus,
    datePreset,
    effectiveStatus,
    dispatch,
  ]);

  const campaignRows = useMemo(
    () =>
      campaigns.ids.map((cacheKey) => {
        const cacheEntry = snapshots[cacheKey] || {};
        const snapshot = cacheEntry.snapshot || {};
        const computed = snapshot.computed || {};
        const campaignData = snapshot.campaign || {};
        const metrics = getPerformanceMetrics(snapshot);
        const metaId = campaignData.id || cacheEntry.objectId;
        const budgetRaw = pickFirstDefined(
          campaignData.daily_budget,
          campaignData.lifetime_budget,
          campaignData.budget,
          computed.budget
        );
        const spendRaw = pickFirstDefined(
          computed.spend,
          campaignData.spend,
          metrics.spend
        );
        // TODO: layer in additional pacing metrics once backend fields are settled.
        return {
          cacheKey,
          objectId: cacheEntry.objectId,
          name: snapshot.campaign?.name || 'Unnamed campaign',
          metaId: metaId || '—',
          budget: formatCurrencyValue(budgetRaw),
          spend: formatCurrencyValue(spendRaw),
          score: formatScore(computed.compliance_score),
          band: computed.compliance_band || 'unknown',
          kind: 'campaign',
          violationsList: Array.isArray(computed.violations) ? computed.violations : [],
          metrics,
          snapshot,
          violations: Array.isArray(computed.violations) ? computed.violations.length : 0,
        };
      }),
    [campaigns.ids, snapshots]
  );

  const filteredCampaignRows = useMemo(() => {
    const normalized = campaignSearch.trim().toLowerCase();
    if (!normalized) {
      return campaignRows;
    }
    return campaignRows.filter((row) => row.name.toLowerCase().includes(normalized));
  }, [campaignRows, campaignSearch]);

  const isCampaignSearchActive = campaignSearch.trim().length > 0;

  const selectedCampaign = useMemo(() => {
    // TODO: collapse the campaign table into a card/accordion once a campaign is selected.
    if (!selectedCampaignKey) {
      return null;
    }
    const cacheEntry = snapshots[selectedCampaignKey];
    if (!cacheEntry) {
      return null;
    }
    const { objectId } = cacheEntry;
    const snapshot = cacheEntry.snapshot || {};
    return {
      cacheEntry,
      objectId,
      snapshot,
      name: snapshot.campaign?.name || objectId,
    };
  }, [selectedCampaignKey, snapshots]);

  const selectedCampaignMetrics = useMemo(() => {
    if (!selectedCampaign) {
      return null;
    }
    return getPerformanceMetrics(selectedCampaign.snapshot);
  }, [selectedCampaign]);

  const selectedCampaignAdsets = useMemo(() => {
    if (!selectedCampaign) {
      return null;
    }
    const bucket = adsetsByCampaign[selectedCampaign.objectId];
    if (!bucket) {
      return null;
    }
    return {
      ...bucket,
      rows: bucket.ids.map((cacheKey) => {
        const cacheEntry = snapshots[cacheKey] || {};
        const snapshot = cacheEntry.snapshot || {};
        const computed = snapshot.computed || {};
        const adsetData = snapshot.adset || {};
        const metrics = getPerformanceMetrics(snapshot);
        const metaId = adsetData.id || cacheEntry.objectId;
        const budgetRaw = pickFirstDefined(
          adsetData.daily_budget,
          adsetData.lifetime_budget,
          adsetData.budget,
          computed.budget
        );
        const spendRaw = pickFirstDefined(
          computed.spend,
          adsetData.spend,
          metrics.spend
        );
        return {
          cacheKey,
          objectId: cacheEntry.objectId,
          name: snapshot.adset?.name || cacheEntry.objectId || 'Unnamed ad set',
          metaId: metaId || '—',
          budget: formatCurrencyValue(budgetRaw),
          spend: formatCurrencyValue(spendRaw),
          score: formatScore(computed.compliance_score),
          band: computed.compliance_band || 'unknown',
          kind: 'adset',
          parentName: selectedCampaign?.name || null,
          violationsList: Array.isArray(computed.violations) ? computed.violations : [],
          metrics,
          snapshot,
          violations: Array.isArray(computed.violations) ? computed.violations.length : 0,
        };
      }),
    };
  }, [adsetsByCampaign, selectedCampaign, snapshots]);

  const selectedAdset = useMemo(() => {
    if (!selectedAdsetKey) {
      return null;
    }
    const cacheEntry = snapshots[selectedAdsetKey];
    if (!cacheEntry) {
      return null;
    }
    const { objectId } = cacheEntry;
    const snapshot = cacheEntry.snapshot || {};
    return {
      cacheEntry,
      objectId,
      snapshot,
      name: snapshot.adset?.name || objectId,
    };
  }, [selectedAdsetKey, snapshots]);

  const selectedAdsetAds = useMemo(() => {
    if (!selectedAdset) {
      return null;
    }
    const bucket = adsByAdset[selectedAdset.objectId];
    if (!bucket) {
      return null;
    }
    return {
      ...bucket,
      rows: bucket.ids.map((cacheKey) => {
        const cacheEntry = snapshots[cacheKey] || {};
        const snapshot = cacheEntry.snapshot || {};
        const computed = snapshot.computed || {};
        const adData = snapshot.ad || {};
        const metrics = getPerformanceMetrics(snapshot);
        const metaId = adData.id || cacheEntry.objectId;
        const spendRaw = pickFirstDefined(
          computed.spend,
          adData.spend,
          metrics.spend
        );
        return {
          cacheKey,
          objectId: cacheEntry.objectId,
          name: adData.name || cacheEntry.objectId || 'Unnamed ad',
          status: adData.status || '—',
          metaId: metaId || '—',
          spend: formatCurrencyValue(spendRaw),
          urlParams: summarizeUrlParams(adData.url_params),
          score: formatScore(computed.compliance_score),
          band: computed.compliance_band || 'unknown',
          kind: 'ad',
          parentName: selectedAdset?.name || null,
          violationsList: Array.isArray(computed.violations) ? computed.violations : [],
          metrics,
          snapshot,
          violations: Array.isArray(computed.violations) ? computed.violations.length : 0,
        };
      }),
    };
  }, [adsByAdset, selectedAdset, snapshots]);

  useEffect(() => {
    const dateChanged = datePresetRef.current !== datePreset;
    const statusChanged = effectiveStatusRef.current !== effectiveStatus;
    if (!dateChanged && !statusChanged) {
      return;
    }
    datePresetRef.current = datePreset;
    effectiveStatusRef.current = effectiveStatus;

    if (selectedCampaign) {
      const bucket = adsetsByCampaign[selectedCampaign.objectId];
      dispatch(
        fetchWatchtowerAdsets({
          campaignId: selectedCampaign.objectId,
          params: {
            page_size: bucket?.pageSize || DEFAULT_PAGE_SIZE,
            date_preset: datePreset,
            effective_status: effectiveStatus,
          },
        })
      );
    }

    if (selectedAdset) {
      const bucket = adsByAdset[selectedAdset.objectId];
      dispatch(
        fetchWatchtowerAds({
          adsetId: selectedAdset.objectId,
          params: {
            page_size: bucket?.pageSize || DEFAULT_PAGE_SIZE,
            date_preset: datePreset,
            effective_status: effectiveStatus,
          },
        })
      );
    }
  }, [
    adsetsByCampaign,
    adsByAdset,
    datePreset,
    effectiveStatus,
    dispatch,
    selectedAdset,
    selectedCampaign,
  ]);

  const campaignLoadMorePending = campaigns.loading && campaignRows.length > 0;
  const adsetLoadMorePending =
    Boolean(selectedCampaignAdsets?.loading && (selectedCampaignAdsets.rows?.length || 0) > 0);
  const adLoadMorePending =
    Boolean(selectedAdsetAds?.loading && (selectedAdsetAds.rows?.length || 0) > 0);

  const handleCampaignRowClick = (cacheKey) => {
    if (selectedCampaignKey === cacheKey) {
      setSelectedCampaignKey(null);
      setSelectedAdsetKey(null);
      setCampaignsCollapsed(false);
      return;
    }
    setSelectedCampaignKey(cacheKey);
    setSelectedAdsetKey(null);
    setCampaignsCollapsed(true);
    const { objectId } = parseWatchtowerCacheKey(cacheKey);
    if (!objectId) {
      return;
    }
    const bucket = adsetsByCampaign[objectId];
    const lastTiming =
      (bucket && (bucket.lastFetched ?? bucket.lastAttempted)) || null;
    const presetMismatch =
      Boolean(bucket?.datePreset && bucket.datePreset !== datePreset);
    const statusMismatch =
      Boolean(bucket?.effectiveStatus && bucket.effectiveStatus !== effectiveStatus);
    const isExpired =
      presetMismatch ||
      statusMismatch ||
      !lastTiming ||
      Date.now() - lastTiming > WATCHTOWER_CACHE_TTL_MS;
    if (isExpired) {
      dispatch(
        fetchWatchtowerAdsets({
          campaignId: objectId,
          params: {
            page_size: bucket?.pageSize || DEFAULT_PAGE_SIZE,
            date_preset: datePreset,
            effective_status: effectiveStatus,
          },
        })
      );
    }
  };

  const handleCampaignListReset = () => {
    setSelectedCampaignKey(null);
    setSelectedAdsetKey(null);
    setCampaignsCollapsed(false);
  };

  const handleBandClick = (event, row) => {
    event.stopPropagation();
    setViolationTarget({
      kind: row.kind,
      name: row.name,
      metaId: row.metaId,
      parentName: row.parentName || null,
      band: row.band,
      score: row.score,
      snapshot: row.snapshot,
      violations: Array.isArray(row.violationsList) ? row.violationsList : [],
    });
  };

  const handleCloseViolations = () => setViolationTarget(null);

  const handleAdsetRowClick = (cacheKey) => {
    if (selectedAdsetKey === cacheKey) {
      setSelectedAdsetKey(null);
      return;
    }
    setSelectedAdsetKey(cacheKey);
    const { objectId } = parseWatchtowerCacheKey(cacheKey);
    if (!objectId) {
      return;
    }
    const bucket = adsByAdset[objectId];
    const lastTiming =
      (bucket && (bucket.lastFetched ?? bucket.lastAttempted)) || null;
    const presetMismatch =
      Boolean(bucket?.datePreset && bucket.datePreset !== datePreset);
    const statusMismatch =
      Boolean(bucket?.effectiveStatus && bucket.effectiveStatus !== effectiveStatus);
    const isExpired =
      presetMismatch ||
      statusMismatch ||
      !lastTiming ||
      Date.now() - lastTiming > WATCHTOWER_CACHE_TTL_MS;
    if (isExpired) {
      dispatch(
        fetchWatchtowerAds({
          adsetId: objectId,
          params: {
            page_size: bucket?.pageSize || DEFAULT_PAGE_SIZE,
            date_preset: datePreset,
            effective_status: effectiveStatus,
          },
        })
      );
    }
  };

  const handleCampaignRefresh = () => {
    dispatch(
      fetchWatchtowerCampaigns({
        params: {
          page_size: campaigns.pageSize || DEFAULT_PAGE_SIZE,
          date_preset: datePreset,
          effective_status: effectiveStatus,
        },
      })
    );
  };

  const handleAdsetRefresh = () => {
    if (!selectedCampaign) {
      return;
    }
    const bucket = adsetsByCampaign[selectedCampaign.objectId];
    dispatch(
      fetchWatchtowerAdsets({
        campaignId: selectedCampaign.objectId,
        params: {
          page_size: bucket?.pageSize || DEFAULT_PAGE_SIZE,
          date_preset: datePreset,
          effective_status: effectiveStatus,
        },
      })
    );
  };

  const handleAdsRefresh = () => {
    if (!selectedAdset) {
      return;
    }
    const bucket = adsByAdset[selectedAdset.objectId];
    dispatch(
      fetchWatchtowerAds({
        adsetId: selectedAdset.objectId,
        params: {
          page_size: bucket?.pageSize || DEFAULT_PAGE_SIZE,
          date_preset: datePreset,
          effective_status: effectiveStatus,
        },
      })
    );
  };

  const handleLoadMoreCampaigns = () => {
    if (!campaigns.nextCursor || campaigns.loading) {
      return;
    }
    const pageSize = campaigns.pageSize || DEFAULT_PAGE_SIZE;
    dispatch(
      fetchWatchtowerCampaigns({
        params: {
          page_size: pageSize,
          cursor: campaigns.nextCursor,
          date_preset: datePreset,
          effective_status: effectiveStatus,
        },
        append: true,
      })
    );
  };

  const handleLoadMoreAdsets = () => {
    if (
      !selectedCampaign ||
      !selectedCampaignAdsets?.nextCursor ||
      selectedCampaignAdsets?.loading
    ) {
      return;
    }
    const pageSize = selectedCampaignAdsets?.pageSize || DEFAULT_PAGE_SIZE;
    dispatch(
      fetchWatchtowerAdsets({
        campaignId: selectedCampaign.objectId,
        params: {
          page_size: pageSize,
          cursor: selectedCampaignAdsets.nextCursor,
          date_preset: datePreset,
          effective_status: effectiveStatus,
        },
        append: true,
      })
    );
  };

  const handleLoadMoreAds = () => {
    if (!selectedAdset || !selectedAdsetAds?.nextCursor || selectedAdsetAds?.loading) {
      return;
    }
    const pageSize = selectedAdsetAds?.pageSize || DEFAULT_PAGE_SIZE;
    dispatch(
      fetchWatchtowerAds({
        adsetId: selectedAdset.objectId,
        params: {
          page_size: pageSize,
          cursor: selectedAdsetAds.nextCursor,
          date_preset: datePreset,
          effective_status: effectiveStatus,
        },
        append: true,
      })
    );
  };

  const renderErrorAlerts = (errors, severity = 'warning') => {
    if (!Array.isArray(errors) || errors.length === 0) {
      return null;
    }
    return errors.map((error, index) => (
      <Alert key={`${error.scope || 'error'}-${index}`} severity={severity} sx={{ mb: 1 }}>
        {error.scope ? `${error.scope}: ` : ''}
        {error.detail || 'No detail provided.'}
      </Alert>
    ));
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Typography variant="h4">Watchtower Dashboard</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            size="small"
            label="Search campaigns"
            placeholder="e.g. Atlanta"
            value={campaignSearch}
            onChange={(event) => setCampaignSearch(event.target.value)}
            sx={{ minWidth: 220 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={activeOnly}
                onChange={(event) => setActiveOnly(event.target.checked)}
                color="primary"
              />
            }
            label="Active only"
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="watchtower-date-preset-label">Date preset</InputLabel>
            <Select
              labelId="watchtower-date-preset-label"
              value={datePreset}
              label="Date preset"
              onChange={(event) => setDatePreset(event.target.value)}
            >
              {DATE_PRESETS.map((preset) => (
                <MenuItem key={preset.value} value={preset.value}>
                  {preset.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={campaigns.loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleCampaignRefresh}
            disabled={campaigns.loading}
          >
            Refresh Campaigns
          </Button>
        </Stack>
      </Stack>
      {campaigns.error && (
        <Alert severity="error">
          {campaigns.error}
        </Alert>
      )}
      {renderErrorAlerts(campaigns.errors)}
      {!campaignsCollapsed && (
        <>
          <Paper elevation={1}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Campaign ID</TableCell>
                    <TableCell align="right">Budget</TableCell>
                    <TableCell align="right">Spend</TableCell>
                    <TableCell>Performance</TableCell>
                    <TableCell align="right">Compliance Score</TableCell>
                    <TableCell align="center">Band</TableCell>
                    <TableCell align="right">Violations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.loading && filteredCampaignRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                          <CircularProgress size={20} />
                          <Typography variant="body2">Loading campaigns...</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : filteredCampaignRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        {isCampaignSearchActive
                          ? 'No campaigns match that search.'
                          : 'No campaigns available.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCampaignRows.map((row) => (
                      <TableRow
                        key={row.cacheKey}
                        hover
                        selected={selectedCampaignKey === row.cacheKey}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleCampaignRowClick(row.cacheKey)}
                      >
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.metaId}</TableCell>
                        <TableCell align="right">{row.budget}</TableCell>
                        <TableCell align="right">{row.spend}</TableCell>
                        <TableCell>{renderPerformanceCell(row.metrics)}</TableCell>
                        <TableCell align="right">{row.score}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.band}
                            size="small"
                            color={bandToColor(row.band)}
                            clickable
                            onClick={(event) => handleBandClick(event, row)}
                            onMouseDown={(event) => event.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell align="right">{row.violations}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          {campaignRows.length > 0 && campaigns.nextCursor && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="text"
                onClick={handleLoadMoreCampaigns}
                disabled={campaigns.loading}
                startIcon={campaignLoadMorePending ? <CircularProgress size={16} /> : null}
              >
                {campaignLoadMorePending ? 'Loading more...' : 'Load more campaigns'}
              </Button>
            </Box>
          )}
        </>
      )}

      {campaignsCollapsed && selectedCampaign && (
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" gutterBottom>
                {selectedCampaign.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Campaign ID: {selectedCampaign.snapshot?.campaign?.id || selectedCampaign.objectId || '—'}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">
                  Score: {formatScore(selectedCampaign.snapshot?.computed?.compliance_score)}
                </Typography>
                <Chip
                  size="small"
                  label={selectedCampaign.snapshot?.computed?.compliance_band || 'unknown'}
                  color={bandToColor(selectedCampaign.snapshot?.computed?.compliance_band)}
                />
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Budget:{' '}
                  {formatCurrencyValue(
                    pickFirstDefined(
                      selectedCampaign.snapshot?.campaign?.daily_budget,
                      selectedCampaign.snapshot?.campaign?.lifetime_budget,
                      selectedCampaign.snapshot?.campaign?.budget,
                      selectedCampaign.snapshot?.computed?.budget
                    )
                  )}
                </Typography>
                <Typography variant="body2">
                  Spend:{' '}
                  {formatCurrencyValue(
                    pickFirstDefined(
                      selectedCampaign.snapshot?.computed?.spend,
                      selectedCampaign.snapshot?.campaign?.spend,
                      selectedCampaignMetrics?.spend
                    )
                  )}
                </Typography>
              </Stack>
              {renderMetricsGrid(selectedCampaignMetrics)}
            </Box>
            <Box>
              <Button variant="text" onClick={handleCampaignListReset}>
                Back to campaigns
              </Button>
              {campaigns.nextCursor && (
                <Button
                  variant="text"
                  sx={{ ml: { sm: 2 }, mt: { xs: 1, sm: 0 } }}
                  onClick={handleLoadMoreCampaigns}
                  disabled={campaigns.loading}
                  startIcon={campaignLoadMorePending ? <CircularProgress size={16} /> : null}
                >
                  {campaignLoadMorePending ? 'Loading more...' : 'Load more campaigns'}
                </Button>
              )}
            </Box>
          </Stack>
        </Paper>
      )}

      {selectedCampaign && (
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h5">
              Ad Sets for {selectedCampaign.name}
            </Typography>
            <Button
              variant="outlined"
              startIcon={
                selectedCampaignAdsets?.loading ? <CircularProgress size={16} /> : <RefreshIcon />
              }
              onClick={handleAdsetRefresh}
              disabled={selectedCampaignAdsets?.loading}
            >
              Refresh Ad Sets
            </Button>
          </Stack>
          {selectedCampaignAdsets?.error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {selectedCampaignAdsets.error}
            </Alert>
          )}
          {renderErrorAlerts(selectedCampaignAdsets?.errors)}
          <Paper elevation={0} variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ad Set</TableCell>
                    <TableCell>Ad Set ID</TableCell>
                    <TableCell align="right">Budget</TableCell>
                    <TableCell align="right">Spend</TableCell>
                    <TableCell>Performance</TableCell>
                    <TableCell align="right">Compliance Score</TableCell>
                    <TableCell align="center">Band</TableCell>
                    <TableCell align="right">Violations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedCampaignAdsets?.loading && (!selectedCampaignAdsets.rows || selectedCampaignAdsets.rows.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                          <CircularProgress size={20} />
                          <Typography variant="body2">Loading ad sets...</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : !selectedCampaignAdsets?.rows || selectedCampaignAdsets.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No ad sets available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedCampaignAdsets.rows.map((row) => (
                      <TableRow
                        key={row.cacheKey}
                        hover
                        selected={selectedAdsetKey === row.cacheKey}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleAdsetRowClick(row.cacheKey)}
                      >
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.metaId}</TableCell>
                        <TableCell align="right">{row.budget}</TableCell>
                        <TableCell align="right">{row.spend}</TableCell>
                        <TableCell>{renderPerformanceCell(row.metrics)}</TableCell>
                        <TableCell align="right">{row.score}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.band}
                            size="small"
                            color={bandToColor(row.band)}
                            clickable
                            onClick={(event) => handleBandClick(event, row)}
                            onMouseDown={(event) => event.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell align="right">{row.violations}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          {selectedCampaignAdsets?.rows &&
            selectedCampaignAdsets.rows.length > 0 &&
            selectedCampaignAdsets.nextCursor && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="text"
                  onClick={handleLoadMoreAdsets}
                  disabled={selectedCampaignAdsets.loading}
                  startIcon={adsetLoadMorePending ? <CircularProgress size={16} /> : null}
                >
                  {adsetLoadMorePending ? 'Loading more...' : 'Load more ad sets'}
                </Button>
              </Box>
            )}
        </Box>
      )}

      {selectedAdset && (
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h5">
              Ads for {selectedAdset.name}
            </Typography>
            <Button
              variant="outlined"
              startIcon={
                selectedAdsetAds?.loading ? <CircularProgress size={16} /> : <RefreshIcon />
              }
              onClick={handleAdsRefresh}
              disabled={selectedAdsetAds?.loading}
            >
              Refresh Ads
            </Button>
          </Stack>
          {selectedAdsetAds?.error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {selectedAdsetAds.error}
            </Alert>
          )}
          {renderErrorAlerts(selectedAdsetAds?.errors)}
          <Paper elevation={0} variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ad</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Ad ID</TableCell>
                    <TableCell align="right">Spend</TableCell>
                    <TableCell>Performance</TableCell>
                    <TableCell>URL Params</TableCell>
                    <TableCell align="right">Compliance Score</TableCell>
                    <TableCell align="center">Band</TableCell>
                    <TableCell align="right">Violations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedAdsetAds?.loading && (!selectedAdsetAds.rows || selectedAdsetAds.rows.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                          <CircularProgress size={20} />
                          <Typography variant="body2">Loading ads...</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : !selectedAdsetAds?.rows || selectedAdsetAds.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No ads available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedAdsetAds.rows.map((row) => (
                      <TableRow key={row.cacheKey}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>{row.metaId}</TableCell>
                        <TableCell align="right">{row.spend}</TableCell>
                        <TableCell>{renderPerformanceCell(row.metrics)}</TableCell>
                        <TableCell>{row.urlParams}</TableCell>
                        <TableCell align="right">{row.score}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.band}
                            size="small"
                            color={bandToColor(row.band)}
                            clickable
                            onClick={(event) => handleBandClick(event, row)}
                            onMouseDown={(event) => event.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell align="right">{row.violations}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          {selectedAdsetAds?.rows &&
            selectedAdsetAds.rows.length > 0 &&
            selectedAdsetAds.nextCursor && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="text"
                  onClick={handleLoadMoreAds}
                  disabled={selectedAdsetAds.loading}
                  startIcon={adLoadMorePending ? <CircularProgress size={16} /> : null}
                >
                  {adLoadMorePending ? 'Loading more...' : 'Load more ads'}
                </Button>
              </Box>
            )}
        </Box>
      )}
      <Drawer
        anchor="right"
        open={Boolean(violationTarget)}
        onClose={handleCloseViolations}
        PaperProps={{ sx: { width: { xs: 320, sm: 380 } } }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              {violationTarget?.kind && (
                <Typography variant="overline" color="text.secondary">
                  {violationTarget.kind.toUpperCase()}
                </Typography>
              )}
              <Typography variant="h6">
                {violationTarget?.name || 'Selection'}
              </Typography>
              {violationTarget?.metaId && (
                <Typography variant="body2" color="text.secondary">
                  ID: {violationTarget.metaId}
                </Typography>
              )}
              {violationTarget?.parentName && (
                <Typography variant="body2" color="text.secondary">
                  Parent: {violationTarget.parentName}
                </Typography>
              )}
            </Box>
            <Stack spacing={1} alignItems="flex-end">
              <Chip
                size="small"
                variant="outlined"
                label={`Score ${violationTarget?.score ?? '—'}`}
              />
              <Chip
                size="small"
                label={violationTarget?.band || 'unknown'}
                color={bandToColor(violationTarget?.band)}
              />
              <Button size="small" onClick={handleCloseViolations}>
                Close
              </Button>
            </Stack>
          </Stack>
          <Divider />
          {violationTarget?.violations && violationTarget.violations.length > 0 ? (
            <Stack spacing={2} sx={{ overflowY: 'auto' }}>
              {violationTarget.violations.map((violation, index) => (
                <Paper
                  key={`${violation.code || 'violation'}-${index}`}
                  variant="outlined"
                  sx={(theme) => ({
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    borderRadius: 2,
                    boxShadow: '0px 4px 12px rgba(15, 23, 42, 0.12)',
                    borderColor: theme.palette.mode === 'dark'
                      ? theme.palette.grey[700]
                      : 'rgba(226, 232, 240, 0.8)',
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(15, 23, 42, 0.75)'
                      : 'linear-gradient(160deg, rgba(255,255,255,0.96), rgba(248, 250, 252, 0.96))',
                    backdropFilter: 'blur(10px)',
                  })}
                >
                  <Typography variant="subtitle2">
                    {violation.code || `Violation ${index + 1}`}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                  >
                    {violation.message || 'No message provided.'}
                  </Typography>
                  {(() => {
                    const expectedValue = describeValue(
                      violation.expected_value ??
                      violation.allowed_value ??
                      violation.required_value ??
                      violation.threshold ??
                      violation.should_be ??
                      violation.expected
                    );
                    let actualValue = describeValue(
                      violation.actual_value ??
                      violation.current_value ??
                      violation.value ??
                      violation.observed_value ??
                      violation.found_value
                    );
                    if (!actualValue && violationTarget?.snapshot) {
                      const resolved = resolveSnapshotValue(
                        violationTarget.snapshot,
                        violation.field,
                        violationTarget.kind
                      );
                      actualValue = describeValue(resolved);
                    }
                    return (expectedValue || actualValue) ? (
                      <Stack spacing={0.5}>
                        {actualValue && (
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                          >
                            <strong>Current:</strong> {actualValue}
                          </Typography>
                        )}
                        {expectedValue && (
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                          >
                            <strong>Expected:</strong> {expectedValue}
                          </Typography>
                        )}
                      </Stack>
                    ) : null;
                  })()}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {violation.severity && (
                      <Chip
                        size="small"
                        color={severityToColor(violation.severity)}
                        label={`Severity: ${violation.severity}`}
                      />
                    )}
                    {violation.target && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Target: ${violation.target}`}
                      />
                    )}
                    {violation.field && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Field: ${violation.field}`}
                      />
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No violations reported for this object.
            </Typography>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default WatchtowerDashboard;
